import { Modal } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useApi from 'hooks/useApi';
import {
  OfferDataForPublisherEvent,
  OfferDataForPublisherEventV2,
} from 'constants/apiRequest.types';
import { BootResponse, OfferData } from 'constants/apiResponses.types';
import {
  EEventsType,
  ELocalStorageKeys,
  EPublisherWebhookEventType,
  EPublisherWebhookEventTypeV2,
  EPublisherWebhookOfferEventType,
  EPublisherWebhookOfferEventTypeV2,
  EQueryParams,
} from 'constants/enums';
import useCustomEvents from 'hooks/useCustomEvents';
import { webStorageUtil } from 'state/webStorage.state.service';
import { AccountApproval } from '@appcharge/shared-ui';
import { useLoginState } from 'state/hooks/login.state.hook';
import { useNavigate } from 'react-router-dom';

interface IAccountApprovalModalProps {
  selectedOffer: OfferData | OfferDataForPublisherEvent | OfferDataForPublisherEventV2 | string;
}

export const AccountApprovalModal: FC<IAccountApprovalModalProps> = ({ selectedOffer }) => {
  const { t } = useTranslation();
  const API = useApi({});
  const publisherMetaData = API.getPublisherMeta.data as BootResponse;
  const customEvents = useCustomEvents();
  const { sendPublisherWebhookEvent, sendPublisherWebhookEventV2 } = useApi({});
  const [isValidated, setIsValidated] = useState<boolean>(false);
  const { logout } = useLoginState();
  const navigate = useNavigate();

  useEffect(() => {
    if (isValidated) return;
    customEvents.sendCustomEvent(EEventsType.DEEPLINK_ACCOUNT_APPROVAL_DISPLAYED, {
      approved_account: true,
      source: 'deeplink',
      source_type: 'dynamo',
    });
    sendPublisherWebhookEvent.mutate({
      eventType: EPublisherWebhookEventType.OFFER,
      data: {
        eventName: EPublisherWebhookOfferEventType.APPROVAL_REQUEST_DISPLAYED,
      },
    });
    sendPublisherWebhookEventV2.mutate({
      eventType: EPublisherWebhookEventTypeV2.OFFERS,
      data: {
        eventName: EPublisherWebhookOfferEventTypeV2.WEBSTORE_APPROVAL_REQUEST_DISPLAYED,
      },
    });
  }, [isValidated]);

  const setSkipParam = () => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.append(EQueryParams.SKIP_APPROVAL, 'true');

    navigate(`${window.location.pathname}?${searchParams.toString()}`, { replace: true });
  };

  const handleAccountApproval = () => {
    setIsValidated(true);

    setSkipParam();

    webStorageUtil.set(
      ELocalStorageKeys.DEEPLINK_APPROVED_SESSION_TOKEN,
      webStorageUtil.get(ELocalStorageKeys.SESSION_TOKEN)
    );

    customEvents.sendCustomEvent(EEventsType.DEEPLINK_ACCOUNT_APPROVAL_APPROVE, {
      approved_account: true,
      offerId: selectedOffer,
      source: 'deeplink',
      source_type: 'dynamo',
    });

    API.sendPublisherWebhookEvent.mutate({
      eventType: EPublisherWebhookEventType.OFFER,
      data: {
        eventName: EPublisherWebhookOfferEventType.APPROVAL_REQUEST_CLOSED,
        reason: 'approve',
      },
    });

    API.sendPublisherWebhookEventV2.mutate({
      eventType: EPublisherWebhookEventTypeV2.OFFERS,
      data: {
        eventName: EPublisherWebhookOfferEventTypeV2.WEBSTORE_APPROVAL_REQUEST_CLOSED,
        reason: 'approve',
      },
    });
  };

  const handleAccountDisapproval = () => {
    customEvents.sendCustomEvent(EEventsType.DEEPLINK_ACCOUNT_APPROVAL_APPROVE, {
      approved_account: false,
      offerId: selectedOffer,
      source: 'deeplink',
      source_type: 'dynamo',
    });

    logout();

    API.sendPublisherWebhookEvent.mutate({
      eventType: EPublisherWebhookEventType.OFFER,
      data: {
        eventName: EPublisherWebhookOfferEventType.APPROVAL_REQUEST_CLOSED,
        reason: 'not_me',
      },
    });

    API.sendPublisherWebhookEventV2.mutate({
      eventType: EPublisherWebhookEventTypeV2.OFFERS,
      data: {
        eventName: EPublisherWebhookOfferEventTypeV2.WEBSTORE_APPROVAL_REQUEST_CLOSED,
        reason: 'not_me',
      },
    });
  };

  return (
    <Modal open={!isValidated} className="account-approval-modal">
      <AccountApproval
        onApproval={handleAccountApproval}
        onDisapproval={handleAccountDisapproval}
        design={publisherMetaData.storeTheme?.login?.accountApprovalModal}
        privacyPolicyUrl={publisherMetaData.storeTheme.general?.footer.privacyPolicyUrl}
        termsAndConditionsUrl={publisherMetaData.storeTheme.general?.footer.termsAndConditionsUrl}
        translations={{
          title: t('accountApprovalModal.title'),
          approve: t('accountApprovalModal.approve'),
          disapprove: t('accountApprovalModal.disapprove'),
          byClicking: t('accountApprovalModal.byClicking'),
          and: t('accountApprovalModal.and'),
          privacyPolicy: t('accountApprovalModal.privacyPolicy'),
          termsAndConditions: t('accountApprovalModal.termsAndConditions'),
        }}
        playerName={webStorageUtil.get(ELocalStorageKeys.PLAYER_DATA)?.playerName}
        playerImage={webStorageUtil.get(ELocalStorageKeys.PLAYER_DATA)?.profileImageUrl}
      />
    </Modal>
  );
};
